import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatBrlFromCents, moneyToCents } from "./currency";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value: unknown) {
  return formatBrlFromCents(moneyToCents(value));
}

export function formatInteger(value: unknown) {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return "0";
  return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(number);
}

export function formatPlate(value: unknown) {
  const plate = maskPlateInput(value);
  return plate || "-";
}

export function maskPlateInput(value: unknown) {
  const chars = String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .split("")
    .reduce<string[]>((plate, char) => {
      const index = plate.length;
      if (index <= 2 && /[A-Z]/.test(char)) return [...plate, char];
      if (index === 3 && /\d/.test(char)) return [...plate, char];
      if (index === 4 && /[A-Z0-9]/.test(char)) return [...plate, char];
      if ((index === 5 || index === 6) && /\d/.test(char)) return [...plate, char];
      return plate;
    }, [])
    .slice(0, 7);

  if (chars.length <= 3) return chars.join("");
  if (chars[4] && /[A-Z]/.test(chars[4])) return chars.join("");
  return `${chars.slice(0, 3).join("")}-${chars.slice(3).join("")}`;
}

export function maskVehicleReferenceInput(value: unknown) {
  const rawValue = String(value ?? "");

  if (rawValue.includes("/")) {
    const [sequencePart = "", ...yearParts] = rawValue.split("/");
    const sequence = sequencePart.replace(/\D/g, "").slice(0, 4);
    const year = yearParts.join("").replace(/\D/g, "").slice(0, 4);
    const formattedSequence = year.length === 4 && sequence ? sequence.padStart(4, "0") : sequence;

    if (year || rawValue.trim().endsWith("/")) return `${formattedSequence}/${year}`;
    return formattedSequence;
  }

  const digits = rawValue.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 4) return digits;
  return `${digits.slice(0, 4)}/${digits.slice(4)}`;
}

export function formatDate(value: unknown) {
  if (!value) return "-";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC" }).format(date);
}

export function formatSequence(sequence?: number | null, subSequence?: number | null) {
  if (!sequence) return "-";
  return `${String(sequence).padStart(3, "0")}/${String(subSequence ?? 1).padStart(3, "0")}`;
}

export const MIN_DATE_FILTER_YEAR = 1900;
export const MAX_DATE_FILTER_YEAR = 2100;

export function isCompleteDateFilter(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

  const [year, month, day] = value.split("-").map(Number);
  if (year < MIN_DATE_FILTER_YEAR || year > MAX_DATE_FILTER_YEAR) return false;

  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

export function sanitizeDateFilter(value: string) {
  return isCompleteDateFilter(value) ? value : "";
}
